import { c as _c } from "react/compiler-runtime";
import { useFormatter, useTranslations } from 'next-intl';
import { Progress } from '@/components/ui/app-progress';
import { Icon } from '@/components/ui/icon';
import { ArrowDataTransferHorizontalIconSolid, LimitationIconSolid } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { outcomeHueToVar } from '@/lib/chart/outcome-hue';
import { useFormattedCurrency } from '@/lib/currency';
import type { OutcomeRowVM } from './derive';
import { formatResolvedPct } from './other-outcomes-format';
import { OutcomeThumb } from './other-outcomes-parts';
interface OtherOutcomeCardProps {
  row: OutcomeRowVM;
  rank: number;
  marketPubkey: string;
}
const METRIC_DIVIDER = '•';
const MAX_CONVICTION_PERCENT = 200;
export function OtherOutcomeCard(t0) {
  const $ = _c(74);
  const {
    row,
    rank,
    marketPubkey
  } = t0;
  const t = useTranslations("market.resolvedPanel");
  const formatter = useFormatter();
  const {
    formatD6
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== row.index || $[1] !== row.label) {
    t1 = {
      title: row.label,
      index: row.index
    };
    $[0] = row.index;
    $[1] = row.label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const colors = useOutcomeColor(t1);
  let t2;
  if ($[3] !== row.finalSharePct) {
    t2 = formatResolvedPct(row.finalSharePct);
    $[3] = row.finalSharePct;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const finalShare = t2;
  const t3 = row.isWinner ? "true" : undefined;
  let t4;
  if ($[5] !== formatter || $[6] !== rank || $[7] !== row.isWinner) {
    t4 = row.isWinner ? undefined : formatter.number(rank);
    $[5] = formatter;
    $[6] = rank;
    $[7] = row.isWinner;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== marketPubkey || $[10] !== row.imageNsfw || $[11] !== row.imageUrl || $[12] !== row.index || $[13] !== row.isWinner || $[14] !== row.label || $[15] !== t4) {
    t5 = <OutcomeThumb marketPubkey={marketPubkey} index={row.index} label={row.label} imageUrl={row.imageUrl} imageNsfw={row.imageNsfw} size="size-12" showSettledBadge={row.isWinner} rankLabel={t4} />;
    $[9] = marketPubkey;
    $[10] = row.imageNsfw;
    $[11] = row.imageUrl;
    $[12] = row.index;
    $[13] = row.isWinner;
    $[14] = row.label;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== row.label) {
    t6 = <span className="break-words text-body font-semibold text-foreground-primary">{row.label}</span>;
    $[17] = row.label;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t) {
    t7 = t("tableVolume");
    $[19] = t;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t7) {
    t8 = <span className="sr-only">{t7}</span>;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Icon icon={ArrowDataTransferHorizontalIconSolid} aria-hidden={true} className="size-3.5 shrink-0" />;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = row.volumeTotalD6 ?? "0";
  let t11;
  if ($[24] !== formatD6 || $[25] !== t10) {
    t11 = formatD6(t10, "compact");
    $[24] = formatD6;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = <span>{t11}</span>;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t8) {
    t13 = <span className="inline-flex items-center gap-1">{t8}{t9}{t12}</span>;
    $[29] = t12;
    $[30] = t8;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span aria-hidden={true} className="mx-0.5 text-foreground-tertiary">{METRIC_DIVIDER}</span>;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t) {
    t15 = t("tablePeak");
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15) {
    t16 = <span className="sr-only">{t15}</span>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Icon icon={LimitationIconSolid} aria-hidden={true} className="size-3.5 shrink-0" />;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== row.peakConsensusPct) {
    t18 = formatResolvedPct(row.peakConsensusPct);
    $[38] = row.peakConsensusPct;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <span>{t18}</span>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t16 || $[43] !== t19) {
    t20 = <span className="inline-flex items-center gap-1">{t16}{t17}{t19}</span>;
    $[42] = t16;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t13 || $[46] !== t20) {
    t21 = <div className="flex flex-wrap items-center gap-1 text-caption tabular-nums text-foreground-secondary">{t13}{t14}{t20}</div>;
    $[45] = t13;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t21 || $[49] !== t6) {
    t22 = <div className="flex min-w-0 flex-1 flex-col gap-1.5">{t6}{t21}</div>;
    $[48] = t21;
    $[49] = t6;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t22 || $[52] !== t5) {
    t23 = <div className="flex min-w-0 items-center gap-3">{t5}{t22}</div>;
    $[51] = t22;
    $[52] = t5;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  const t24 = Math.min(MAX_CONVICTION_PERCENT, Math.max(0, row.finalSharePct));
  const t25 = row.label;
  let t26;
  if ($[54] !== t) {
    t26 = t("tableFinal");
    $[54] = t;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  const t27 = `${t25}: ${t26}`;
  let t28;
  if ($[56] !== colors.hue) {
    t28 = outcomeHueToVar(colors.hue, "text");
    $[56] = colors.hue;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t28) {
    t29 = {
      backgroundColor: t28
    };
    $[58] = t28;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== finalShare || $[61] !== t24 || $[62] !== t27 || $[63] !== t29) {
    t30 = <Progress value={t24} max={MAX_CONVICTION_PERCENT} aria-label={t27} aria-valuetext={finalShare} className="h-1.5 min-w-0 flex-1 bg-background-tertiary" indicatorStyle={t29} />;
    $[60] = finalShare;
    $[61] = t24;
    $[62] = t27;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== finalShare) {
    t31 = <span className="shrink-0 text-caption tabular-nums text-foreground-secondary">{finalShare}</span>;
    $[65] = finalShare;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== t30 || $[68] !== t31) {
    t32 = <div className="flex min-w-0 items-center gap-3">{t30}{t31}</div>;
    $[67] = t30;
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t23 || $[71] !== t3 || $[72] !== t32) {
    t33 = <li data-testid="resolved-panel-outcome-row" data-winner={t3} className="flex min-w-0 flex-col gap-3">{t23}{t32}</li>;
    $[70] = t23;
    $[71] = t3;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  return t33;
}